import type { FetchRequestError } from "@lib/errors";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";

/**
 * Machine-readable error codes returned by
 * ZendeskSunshineIntegrationController#connect_integration. Mirrors
 * Braze::SupportTools::ZendeskSunshine::ConnectProvisioner and the
 * controller's rescue clauses.
 */
export type ZendeskSunshineConnectErrorCode =
  | "invalid_subdomain"
  | "multi_convo_disabled"
  | "zendesk_connect_failed"
  | "zendesk_unavailable"
  | "invalid_record";

/**
 * Discriminated union with one FetchUserEncodedError member per backend
 * connect error code, so `switch (error.errorCode)` narrows and an
 * exhaustiveCheck default branch fails to compile when the backend adds a
 * code without a frontend handler.
 */
export type PostAlloysZendeskSunshineErrors = {
  [Code in ZendeskSunshineConnectErrorCode]: FetchUserEncodedError<Code>;
}[ZendeskSunshineConnectErrorCode];

export const isPostAlloysZendeskSunshineError = (
  error: FetchRequestError
): error is PostAlloysZendeskSunshineErrors => {
  return error instanceof FetchUserEncodedError;
};

export interface ZendeskSunshineIntegration {
  company_id: string;
  app_group_id: string;
  integration_id: string;
  subdomain: string;
  sunshine_app_id: string;
  key_id: string;
  /** Always "/REDACTED/" when stored; the plaintext secret is never returned */
  key_secret: string | null;
  /** Always "/REDACTED/" when stored; the plaintext secret is never returned */
  webhook_secret: string | null;
  custom_integration_id: string | null;
  connected_at: string | null;
}

export interface GetAlloysZendeskSunshineAppGroupIdResult {
  /** {} when the workspace has never connected */
  integration: Partial<ZendeskSunshineIntegration>;
}

export interface PostAlloysZendeskSunshineAppGroupIdResult {
  integration: ZendeskSunshineIntegration;
}

export interface PostAlloysZendeskSunshineAppGroupIdProps {
  appGroupId: string;
  subdomain: string;
  sunshineAppId: string;
  keyId: string;
  keySecret: string;
}
